import { useEffect, useState } from "react";
import {
  TrendingUp, DollarSign, Target, Trophy, XCircle, MonitorSmartphone, Syringe,
  Route, Receipt, Coins, GraduationCap, CalendarClock, CheckSquare, Building2,
  ArrowUpRight, Package, Users,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import api from "../lib/api";
import { fmtCurrency, fmtNumber } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { Button } from "../components/ui/button";

function Kpi({ icon: Icon, label, value, accent = "navy", testid, sub }) {
  const accents = {
    navy: "bg-navy-900 text-white",
    blue: "bg-brand text-white",
    emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-500 text-white",
    violet: "bg-violet-600 text-white",
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>
        {sub && <span className="text-xs font-medium text-emerald-600 flex items-center gap-0.5"><ArrowUpRight className="h-3 w-3" />{sub}</span>}
      </div>
      <div className="mt-4">
        <div className="font-display text-2xl font-bold text-navy-900 tracking-tight">{value}</div>
        <div className="text-xs text-slate-500 mt-0.5">{label}</div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [s, setS] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setS(r.data)).catch(() => {});
  }, []);

  if (!s) {
    return <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-32 bg-white rounded-xl border border-slate-200 animate-pulse" />))}</div>;
  }

  const stageColors = ["#94A3B8", "#60A5FA", "#3B82F6", "#2563EB", "#1D4ED8", "#10B981", "#EF4444"];
  const isEmpty = (s.open_opportunities + s.won_opportunities + s.lost_opportunities === 0)
    && s.new_clinics === 0 && s.workshops_scheduled === 0 && s.tasks_due === 0 && s.pipeline_value === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy-900">
            Welcome back, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Here's what's happening across Dermalis today.</p>
        </div>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-sm text-slate-400">{new Date().toLocaleDateString("en-NZ", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
          <Button onClick={() => navigate("/todays-plan")} className="bg-navy-900 hover:bg-navy-800" size="sm" data-testid="dashboard-print-plan-btn">
            <ClipboardList className="h-4 w-4 mr-1.5" /> Print Plan
          </Button>
        </div>
      </div>

      {isEmpty && (
        <div className="bg-navy-900 rounded-xl p-6 text-white flex flex-col sm:flex-row sm:items-center gap-4" data-testid="dashboard-empty-state">
          <div className="flex-1">
            <h3 className="font-display text-lg font-semibold">Your CRM is ready — let's add your first records</h3>
            <p className="text-sm text-blue-100/80 mt-1">The dashboard will populate as you add clinics, log opportunities and record activity. Start here:</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => navigate("/clinics")} className="bg-white text-navy-900 hover:bg-blue-50" size="sm" data-testid="empty-add-clinic">Add a clinic</Button>
            <Button onClick={() => navigate("/pipeline")} variant="outline" className="border-white/30 text-white hover:bg-white/10" size="sm" data-testid="empty-add-opp">Create opportunity</Button>
          </div>
        </div>
      )}

      {/* Revenue row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={DollarSign} label="Revenue This Month" value={fmtCurrency(s.revenue_month)} accent="navy" sub="MTD" testid="kpi-revenue-month" />
        <Kpi icon={TrendingUp} label="Revenue This Quarter" value={fmtCurrency(s.revenue_quarter)} accent="blue" testid="kpi-revenue-quarter" />
        <Kpi icon={Trophy} label="Revenue This Year" value={fmtCurrency(s.revenue_year)} accent="emerald" testid="kpi-revenue-year" />
        <Kpi icon={Target} label="Pipeline Value" value={fmtCurrency(s.pipeline_value)} accent="violet" testid="kpi-pipeline" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-semibold text-navy-900">Revenue Trend</h3>
              <p className="text-xs text-slate-400">Closed-won revenue, last 6 months</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={s.revenue_trend}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2563EB" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94A3B8" }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Area type="monotone" dataKey="revenue" stroke="#2563EB" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-1">Pipeline by Stage</h3>
          <p className="text-xs text-slate-400 mb-4">Value per stage</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={s.pipeline_by_stage} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="stage" tick={{ fontSize: 11, fill: "#64748B" }} width={90} axisLine={false} tickLine={false} />
              <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 13 }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {s.pipeline_by_stage.map((_, i) => <Cell key={i} fill={stageColors[i]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Operational KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Target} label="Open Opportunities" value={fmtNumber(s.open_opportunities)} accent="blue" testid="kpi-open" />
        <Kpi icon={Trophy} label="Won Opportunities" value={fmtNumber(s.won_opportunities)} accent="emerald" testid="kpi-won" />
        <Kpi icon={XCircle} label="Lost Opportunities" value={fmtNumber(s.lost_opportunities)} accent="red" testid="kpi-lost" />
        <Kpi icon={MonitorSmartphone} label="Device Sales" value={fmtCurrency(s.device_sales)} accent="navy" testid="kpi-device" />
        <Kpi icon={Syringe} label="Injectable Sales" value={fmtCurrency(s.injectable_sales)} accent="violet" testid="kpi-injectable" />
        <Kpi icon={Route} label="Mileage This Month" value={`${fmtNumber(s.mileage_month)} km`} accent="amber" testid="kpi-mileage" />
        <Kpi icon={Receipt} label="Expenses This Month" value={fmtCurrency(s.expenses_month)} accent="red" testid="kpi-expenses" />
        <Kpi icon={Coins} label="Commission Forecast" value={fmtCurrency(s.commission_forecast)} accent="emerald" testid="kpi-commission" />
        <Kpi icon={GraduationCap} label="Workshops Scheduled" value={fmtNumber(s.workshops_scheduled)} accent="blue" testid="kpi-workshops" />
        <Kpi icon={CalendarClock} label="Upcoming Meetings" value={fmtNumber(s.upcoming_meetings)} accent="navy" testid="kpi-meetings" />
        <Kpi icon={CheckSquare} label="Tasks Due" value={fmtNumber(s.tasks_due)} accent="amber" testid="kpi-tasks" />
        <Kpi icon={Building2} label="New Clinics Added" value={fmtNumber(s.new_clinics)} accent="emerald" testid="kpi-clinics" />
      </div>

      {/* Top performers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-4 flex items-center gap-2"><Package className="h-4 w-4 text-brand" /> Top Performing Products</h3>
          <div className="space-y-3">
            {s.top_products.length === 0 && <p className="text-sm text-slate-400">No data yet</p>}
            {s.top_products.map((p, i) => (
              <div key={p.name} className="flex items-center gap-3">
                <span className="h-7 w-7 rounded-md bg-slate-100 text-navy-900 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-navy-900 truncate">{p.name}</span>
                <span className="text-sm font-semibold text-slate-600">{fmtCurrency(p.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
          <h3 className="font-display font-semibold text-navy-900 mb-4 flex items-center gap-2"><Users className="h-4 w-4 text-brand" /> Top Performing BDMs</h3>
          <div className="space-y-3">
            {s.top_bdms.length === 0 && <p className="text-sm text-slate-400">No data yet</p>}
            {s.top_bdms.map((b, i) => (
              <div key={b.name} className="flex items-center gap-3">
                <span className="h-7 w-7 rounded-md bg-navy-900 text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span className="flex-1 text-sm font-medium text-navy-900 truncate">{b.name}</span>
                <span className="text-sm font-semibold text-slate-600">{fmtCurrency(b.revenue)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
